import React, { useEffect } from 'react';
import { useSelector, useLogger, useI18n, useCurrency, useLicenses, useDispatch } from '~Hooks';

import {
  printHeader,
  printExtraSpace,
  printTestContent,
  printOrderComment,
  divider,
  processGroupedStationItems,
  generatePrintContent,
  removeSpecialCharactersForEpson,
  courseTypeMessages,
  courseGroupContent,
  stationTitle,
  formatTicketMessages,
} from './Layout';

import {
  getBongData_stations as Station,
  getBongData_categories,
  getBongData_courseTypes,
  getBongData_stations_categories,
  getBongData_stations_items,
} from './__queries__';
import _ from 'lodash';
import { LocalItem, TicketMessage } from '~Types';
import { useStorage } from '~Components/Storage';
import { Printer } from '~reducers';
import { useOrderStatusApi, useOrderStatusUniverse } from '~Components/OrderStatusUniverse';
import { ORDER_ORIGIN, ORDER_STATUS } from '@baemingo/order-status-events/dist/types';
import { SERVICE_TYPE } from '~../__queries__';
import { isServiceChargeItem } from '~Utils/serviceCharge';
import { PrintRow } from '~Components/PrinterManager';
import { DateTime } from 'luxon';
import { useBongHistory } from '~Hooks';
import useConfirm from '~Hooks/useConfirm';
import { useLocation } from 'react-router';

// THIS FILE WILL BE DEPRECATED SOON

const groupByCourseType = (currentItems: LocalItem[]) => {
  const sortedItems = _.sortBy(currentItems, (item) =>
    item.courseType?.order ? item.courseType?.order : null,
  );
  return _.groupBy(sortedItems, (item) => _.get(item.courseType, 'label', 'noType'));
};

const getItemCourseId = (item: LocalItem) => item.type ?? item.courseType?.id ?? null;

export interface BongGroupOrderInfo {
  title: string;
  date: string;
  serviceType: string;
  estimatedTime: string | undefined;
  desiredTime: string | undefined;
  deliveryLocation: string | undefined;
  staffId?: string;
  coverage?: number;
  tableName?: string;
  tableId?: string | null;
  orderNumber?: string;
  realId?: string;
  comment?: string;
  origin?: ORDER_ORIGIN | null;
  floorplan?: string;
}

interface Props {
  groupId: string;
  items: LocalItem[];
  messages?: LocalItem[];
  stations: Station[];
  categories: getBongData_categories[];
  onlyDigitalBongs?: boolean;
  onlyPhysicalBongs?: boolean;
  orderInfo: BongGroupOrderInfo;
  onCompleted: () => void;
  ticketMessages?: TicketMessage[];
  allCourseTypes: getBongData_courseTypes[];
  skipConfirmation?: boolean;
}

const BongGroup: React.FunctionComponent<Props> = ({
  groupId,
  items,
  messages,
  stations,
  categories,
  orderInfo,
  onCompleted,
  onlyDigitalBongs,
  onlyPhysicalBongs,
  ticketMessages,
  allCourseTypes,
  skipConfirmation,
}) => {
  const [, , venueId] = location.hash.split('/');
  const [storage] = useStorage();
  const licenses = useLicenses();
  const printers = useSelector((state) => state.printers);
  const companyStaff = useSelector((state) => state.companyStaff);
  const logger = useLogger('printer');
  const orderStatusApi = useOrderStatusApi(venueId);
  const orderStatus = useOrderStatusUniverse();
  const currency = useCurrency();
  const { i18n } = useI18n();
  const bongHistory = useBongHistory(groupId, orderInfo.title);
  const { confirm } = useConfirm();
  const currentRoute = useLocation();

  // Check if we are in EPOS (express or self-service-kiosk) where we should skip the confirmation
  const isEpos = ['express', 'self-service-kiosk'].some((path) =>
    currentRoute.pathname.includes(path),
  );

  const executePrint = (printContent: PrintRow[], printer: Printer, orderId?: string) => {
    const filteredPrintContent = printContent.filter(
      (item): item is PrintRow => item !== undefined && item.content !== undefined,
    );

    window.printerManager?.initiatePrint({
      data: filteredPrintContent,
      printerId: printer.id,
      printType: 'BONG',
      orderId,
    });
  };

  const printTestBong = (
    printer: Printer,
    printerStations: any[],
    allStationItems: getBongData_stations_items[],
    allStationCategories: getBongData_stations_categories[],
  ) => {
    const testContent = printTestContent(
      printer,
      printerStations,
      allStationItems,
      allStationCategories,
      i18n,
      storage.taxComplianceDeviceName || undefined,
    );

    let printContent = [...testContent.flat(), ...printExtraSpace()];

    printContent = printContent?.filter((n) => n);

    window.printerManager?.initiatePrint({
      printerId: printer.id,
      data: printContent,
      printType: 'BONG',
    });
  };

  const printBong = (
    printer: Printer,
    header: PrintRow[],
    printerStationsAreUsed: boolean,
    orderId?: string,
  ) => {
    // Get the stations for this printer
    const printerStations = stations.filter((station) => printer.stations.includes(station.id));

    // Get active items, categories and service types for this station
    const allStationServiceTypes = _.flatMap(printerStations, (station) => station.serviceType);
    const allStationOrigins = _.flatMap(printerStations, (station) => station.origin);
    const allStationItems = _.flatMap(printerStations, (station) => station.items);
    const allStationCategories = _.flatMap(printerStations, (station) => station.categories);

    // Test printers by printing a bong on all printers with their respective items and categories
    if (orderInfo.serviceType === 'testPrinters') {
      printTestBong(printer, printerStations, allStationItems, allStationCategories);

      return;
    }

    // Don't print if stations doesn't include order service type
    if (
      orderInfo.serviceType &&
      allStationServiceTypes.length > 0 &&
      !allStationServiceTypes.includes(orderInfo.serviceType as SERVICE_TYPE)
    ) {
      logger.info('[DEBUG MODE] Not printing because stations doesnt include order service type', {
        orderInfoServiceType: orderInfo.serviceType,
      });
      bongHistory.savePrintAttempt({
        printerId: printer?.id || '',
        reason: i18n('BongHistory.Reason.ServiceTypeNotIncluded', orderInfo.serviceType),
        hasBeenAttempted: false,
      });
      return;
    }

    // Don't print if stations doesn't include order origin
    if (
      orderInfo.origin &&
      allStationOrigins.length > 0 &&
      !allStationOrigins.includes(orderInfo.origin)
    ) {
      logger.info('[DEBUG MODE] Not printing because stations doesnt include order origin', {
        orderInfoOrigin: orderInfo.origin,
      });
      bongHistory.savePrintAttempt({
        printerId: printer.id,
        reason: i18n('BongHistory.Reason.OriginNotIncluded', orderInfo.origin),
        hasBeenAttempted: false,
      });
      return;
    }

    logger.info('[DEBUG MODE] Start kitchen ticket for printer', {
      ticketData: {
        printer,
        printerStations,
        items,
        messages,
        orderInfo,
        printingSettings: {
          bongPrintsEnabled: storage.enableBongPrints,
          bongGroupByStation: storage.bongGroupByStation,
          bongPrintByStation: storage.bongPrintByStation,
          bongGroupByCategory: storage.bongGroupByCategory,
          printerStationsAreUsed: printerStationsAreUsed,
          onlyDigitalBongs,
          onlyPhysicalBongs,
        },
        ticketMessages,
      },
    });

    /**
     * If we dont use stations at all, print everything on the bong.
     * Otherwise, filter.
     */
    const [stationItems, homelessItems] = !printerStationsAreUsed
      ? [items, []]
      : // Otherwise, filter for relevant items
        _.partition(items, (item) => {
          return (
            allStationItems.some((stItem) => stItem.id === item.itemId) ||
            allStationCategories.some(
              (stCat) => stCat.name === item.categoryId || stCat.id === item.categoryId,
            )
          );
        });

    const stationItemIds = stationItems.map((si) => si.itemId);

    // Dont print at all if there are no relevant items to print
    logger.info('[DEBUG MODE] stationItems', {
      stationItems,
    });

    if (!stationItems.length) {
      logger.info('Kitchen ticket - Not printing because there are no relevant items to print', {
        homelessItems,
      });
      bongHistory.savePrintAttempt({
        printerId: printer.id,
        reason: i18n('BongHistory.Reason.NoRelevantItems'),
        hasBeenAttempted: false,
      });
      return;
    }

    // Add comment from app orders to bong
    const orderComment = orderInfo.comment
      ? printOrderComment(orderInfo.comment, storage.bongGroupByStation)
      : [];

    // Print separate ticket per course type for each printer station
    // Example: 1 printer, station: Food, Drinks would print the same as having 2 printers, one for Food and one for Drinks
    // Can also be used to print copies of the same ticket if you add two identical stations to the same printer
    if (storage.bongGroupByStation && storage.bongPrintByStation) {
      printer.stations.forEach((station) => {
        const foundStation = stations.find((s) => s.id === station);
        const stationName = foundStation?.name;
        const stItems = foundStation?.items;
        const stCategories = foundStation?.categories;

        const itemsForStation = [...items].filter(
          (item) =>
            stItems?.some((stItem) => stItem.id === item.itemId) ||
            stCategories?.some(
              (stCat) => stCat.id === item.categoryId || stCat.name === item.categoryId,
            ),
        );

        // If the items for this station does not have any course types we don't want to show courses from other stations
        const hasItemsWithCourseType = itemsForStation.some((item) => item.courseType);
        const relevantItems = hasItemsWithCourseType ? [...items] : itemsForStation;

        const newTypeGrouped = groupByCourseType([...relevantItems]);
        const newTypeGroupedMessages = groupByCourseType(messages || []);

        if (!foundStation) {
          logger.warn('[DEBUG MODE] Station does not exist', {
            stations,
            station,
          });
          bongHistory.savePrintAttempt({
            printerId: printer.id,
            reason: i18n('BongHistory.Reason.StationNotExists', station),
            hasBeenAttempted: false,
          });
          return;
        }

        Object.entries(newTypeGrouped).forEach(([courseType, courseItems]) => {
          const groupContent = courseGroupContent({
            courseItems,
            courseType,
            stationItemIds: itemsForStation.map((si) => si.itemId),
            storage,
            categories,
            i18n,
            currency,
            printerStations: [foundStation],
          });

          // groupContent will be empty if there are no order items for this course type on this printer station
          // itemsForStation will be empty if there are no order items connected to this station
          if (groupContent.length === 0 || itemsForStation.length === 0) {
            logger.info('[DEBUG MODE] Not printing because there are no relevant items to print', {
              allGroups: newTypeGrouped,
              courseType,
              courseItems,
              stationItemIds,
            });

            bongHistory.savePrintAttempt({
              printerId: printer.id,
              reason: `${
                courseType === 'noType'
                  ? i18n('BongHistory.Reason.NoCourseTypeItems.NoType')
                  : i18n('BongHistory.Reason.NoCourseTypeItems', courseType)
              } "${stationName}"`,
              hasBeenAttempted: false,
            });
            return;
          }

          //Filter to only show relevant ticket messages
          const foundCourseType = allCourseTypes.find((ct) => ct.label === courseType);
          const filteredTicketMessages = ticketMessages
            ? ticketMessages?.filter((tm) => !tm.courseId || foundCourseType?.id === tm.courseId)
            : [];

          let printContent = [
            ...header,
            ...stationTitle(i18n, stationName),
            ...courseTypeMessages({
              courseType,
              typeGroupedMessages: newTypeGroupedMessages,
            }).flat(),
            ...groupContent.flat(),
            ...formatTicketMessages({
              ticketMessages: filteredTicketMessages,
              courseTypeLabel: courseType,
              useTopDivider: true,
            }).flat(),
            ...orderComment,
            ...printExtraSpace(),
          ];

          printContent = printContent.filter((n) => n);

          removeSpecialCharactersForEpson({ printContent, printer });

          // PRINT HERE
          executePrint(printContent, printer, orderId);
          bongHistory.savePrintAttempt({
            printerId: printer.id,
            reason: `${
              courseType === 'noType'
                ? i18n('BongHistory.Reason.PrintedCourseTypeStation.NoType')
                : i18n('BongHistory.Reason.PrintedCourseTypeStation', courseType)
            } "${stationName}"`,
          });
        });
      });
      return;
    }

    // Groups all printer stations together and then prints a separate ticket per course type
    if (storage.bongGroupByStation) {
      // Group together by course type
      const typeGrouped = groupByCourseType(items);
      const typeGroupedMessages = groupByCourseType(messages || []);

      Object.entries(typeGrouped).forEach(([courseType, courseItems]) => {
        const groupContent = courseGroupContent({
          courseItems,
          courseType,
          stationItemIds,
          storage,
          categories,
          i18n,
          currency,
          printerStations,
        });

        // groupContent will be empty if there are no order items for this course type on the printer stations
        if (groupContent.length === 0) {
          logger.info('[DEBUG MODE] Not printing because there are no relevant items to print', {
            allGroups: typeGrouped,
            courseType,
            courseItems,
            stationItemIds,
          });
          bongHistory.savePrintAttempt({
            printerId: printer.id,
            reason:
              courseType === 'noType'
                ? i18n('BongHistory.Reason.NoCourseTypeItemsAnyStation.NoType')
                : i18n('BongHistory.Reason.NoCourseTypeItemsAnyStation', courseType),
            hasBeenAttempted: false,
          });
          return;
        }

        //Filter to only show relevant ticket messages
        const foundCourseType = allCourseTypes.find((ct) => ct.label === courseType);
        const filteredTicketMessages = ticketMessages
          ? ticketMessages?.filter((tm) => !tm.courseId || foundCourseType?.id === tm.courseId)
          : [];

        let printContent = [
          ...header,
          ...divider,
          ...courseTypeMessages({ courseType, typeGroupedMessages }).flat(),
          ...groupContent.flat(),
          ...formatTicketMessages({
            ticketMessages: filteredTicketMessages,
            courseTypeLabel: courseType,
            useTopDivider: true,
          }).flat(),
          ...orderComment,
          ...printExtraSpace(),
        ];

        printContent = printContent.filter((n) => n);

        removeSpecialCharactersForEpson({ printContent, printer });

        // PRINT HERE
        executePrint(printContent, printer, orderId);
        bongHistory.savePrintAttempt({
          printerId: printer.id,
          reason:
            courseType === 'noType'
              ? i18n('BongHistory.Reason.PrintedCourseType.NoType')
              : i18n('BongHistory.Reason.PrintedCourseType', courseType),
        });
      });
      return;
    }

    // Print 1 ticket per printer station
    // Example: 1 printer, station: Food, Drinks would print the same as having 2 printers, one for Food and one for Drinks
    // Can also be used to print copies of the same ticket if you add two identical stations to the same printer
    if (storage.bongPrintByStation) {
      printer.stations.forEach((station) => {
        const foundStation = stations.find((s) => s.id === station);
        const stationName = foundStation?.name;
        const stItems = foundStation?.items;
        const stCategories = foundStation?.categories;

        if (!foundStation) {
          logger.warn('[DEBUG MODE] Station does not exist', {
            stations,
            station,
          });
          bongHistory.savePrintAttempt({
            printerId: printer.id,
            reason: i18n('BongHistory.Reason.StationNotExists', station),
            hasBeenAttempted: false,
          });
          return;
        }

        const itemsForStation = [...items].filter(
          (item) =>
            stItems?.some((stItem) => stItem.id === item.itemId) ||
            stCategories?.some(
              (stCat) => stCat.id === item.categoryId || stCat.name === item.categoryId,
            ),
        );

        // If the items for this station does not have any course types we don't want to show courses from other stations
        const hasItemsWithCourseType = itemsForStation.some((item) => item.courseType);
        const relevantItems = hasItemsWithCourseType ? [...items] : itemsForStation;

        const newTypeGrouped = groupByCourseType([...relevantItems]);

        const groupedStationItems = processGroupedStationItems({
          typeGrouped: newTypeGrouped,
          storage,
          categories,
          stationItemIds: itemsForStation.map((si) => si.itemId),
          i18n,
          currency,
          printerStations: [foundStation],
        });

        // groupedStationItems will be empty if there are no order items connected to this station or other relevant course items from other stations
        // itemsForStation will be empty if there are no order items connected to this station
        // groupedStationItems can have items from other stations but if the itemsForStation is empty we don't want to print anything
        if (groupedStationItems.length === 0 || itemsForStation.length === 0) {
          logger.info('[DEBUG MODE] Not printing because there are no relevant items to print', {
            allGroups: newTypeGrouped,
            station,
            itemsForStation,
            stationItemIds,
          });
          bongHistory.savePrintAttempt({
            printerId: printer.id,
            reason: i18n('BongHistory.Reason.NoItemsForStation', stationName),
            hasBeenAttempted: false,
          });
          return;
        }

        const relevantCourseTypes = itemsForStation.map((item) => item.courseType?.id);

        const filteredTicketMessages = ticketMessages?.filter(
          (tm) => !tm.courseId || relevantCourseTypes.includes(tm.courseId),
        );

        const printContent = generatePrintContent({
          header,
          stationName,
          messages,
          groupedStationItems,
          orderComment,
          i18n,
          ticketMessages: filteredTicketMessages ?? [],
        });

        removeSpecialCharactersForEpson({
          printContent,
          printer,
        });

        // PRINT HERE
        executePrint(printContent, printer, orderId);
        bongHistory.savePrintAttempt({
          printerId: printer.id,
          reason: i18n('BongHistory.Reason.PrintedStation', stationName),
        });
      });
      return;
    }

    // Default:  Print 1 ticket with all printer stations combined
    const groupedStationItems = processGroupedStationItems({
      typeGrouped: groupByCourseType(items),
      storage,
      categories,
      stationItemIds,
      i18n,
      currency,
      printerStations,
    });

    const itemsForStation = [...items].filter(
      (item) =>
        allStationItems?.some((stItem) => stItem.id === item.itemId) ||
        allStationCategories?.some(
          (stCat) => stCat.id === item.categoryId || stCat.name === item.categoryId,
        ),
    );

    const relevantCourseTypes = itemsForStation.map((item) => item.courseType?.id);

    const filteredTicketMessages = ticketMessages?.filter(
      (tm) => !tm.courseId || relevantCourseTypes.includes(tm.courseId),
    );

    const printContent = generatePrintContent({
      header,
      messages,
      groupedStationItems,
      orderComment,
      ticketMessages: filteredTicketMessages ?? [],
      i18n,
    });

    removeSpecialCharactersForEpson({ printContent, printer });

    logger.info('[DEBUG MODE] Printing bong', { data: { printContent, printer } });

    // PRINT HERE
    executePrint(printContent, printer, orderId);
    bongHistory.savePrintAttempt({
      printerId: printer.id,
      reason: i18n('BongHistory.Reason.PrintedCombined'),
    });
  };

  useEffect(() => {
    const executeWithConfirmation = async () => {
      // Get staff name if it exists
      const staffName =
        orderInfo.staffId && companyStaff?.staffs.find((s) => s.id === orderInfo.staffId)?.name;

      const header = printHeader(
        orderInfo,
        storage,
        i18n,
        staffName,
        storage.taxComplianceDeviceName || undefined,
      );

      // This is true if any connected printer has an assigned station
      const printerStationsAreUsed =
        printers.some((printer) => printer.stations.length > 0) ||
        (licenses.hasKDS && stations.length > 0);
      const isAppOrder = !!orderInfo.realId;

      // Get all station items and cateogires
      const allStationItems = _.flatMap(stations, (station) => station.items);
      const allStationCategories = _.flatMap(stations, (station) => station.categories);

      logger.info('[DEBUG MODE] all stations', {
        stations,
      });

      // Partition items based on if they belong to a station or not
      // If we are not using stations or its an app order, add all items
      const [itemsToAdd, homelessItems] =
        !printerStationsAreUsed || (isAppOrder && !storage.enableNewKDSAppOrders)
          ? [items, []]
          : // Otherwise, filter for relevant items
            _.partition(items, (item) => {
              return (
                allStationItems.some((stItem) => stItem.id === item.itemId) ||
                allStationCategories.some(
                  (stCat) => stCat.id === item.categoryId || stCat.name === item.categoryId,
                )
              );
            });

      logger.info('[DEBUG MODE] Partitioned items', {
        itemsToAdd,
        homelessItems,
      });
      // These items won't be added to the KDS, logged for debug purposes
      if (homelessItems.length > 0 && isAppOrder) {
        logger.warn('Skipped items for app order ', {
          items: homelessItems,
          orderRealId: orderInfo.realId,
        });
      }

      // Store KDS orderId and pass it along to any eventual physical bong prints
      let orderId: string | undefined;

      const hasKDS =
        licenses.hasKDS ||
        licenses.atleastOneDeviceHasKDS ||
        licenses.atleastOneDeviceHasCustomerDisplay;

      logger.info('[DEBUG MODE] Adding items to KDS', {
        hasKDS,
        itemsToAdd,
        onlyPhysicalBongs,
      });

      // If we use KDS and have bongable items, add bong to KDS.
      if (hasKDS && itemsToAdd.length > 0 && !onlyPhysicalBongs) {
        logger.info("[DEBUG MODE] We're using KDS and have bongable items, add bong to KDS");

        const nonServiceChargeItems = itemsToAdd.filter((item) => !isServiceChargeItem(item.title));

        if (storage.KDSCourseOrdersMode) {
          const groupedByCourse = _.groupBy(nonServiceChargeItems, (item) => getItemCourseId(item) || 'NO_COURSE');
          logger.info('[DEBUG MODE] KDS course order mode grouping', {
            holdFirstCourse: storage.holdFirstCourse,
            courseKeys: Object.keys(groupedByCourse),
            groupedItems: Object.fromEntries(
              Object.entries(groupedByCourse).map(([key, value]) => [
                key,
                value.map((item) => ({ id: item.id, title: item.title, courseId: getItemCourseId(item) })),
              ]),
            ),
          });
          const sortedCourseKeys = Object.keys(groupedByCourse).sort((a, b) => {
            const aOrder = groupedByCourse[a][0]?.courseType?.order ?? Number.MAX_SAFE_INTEGER;
            const bOrder = groupedByCourse[b][0]?.courseType?.order ?? Number.MAX_SAFE_INTEGER;
            return aOrder - bOrder;
          });

          sortedCourseKeys.forEach((courseKey, index) => {
            const courseItems = groupedByCourse[courseKey];
            if (!courseItems || courseItems.length === 0) {
              return;
            }

            const courseId = courseKey === 'NO_COURSE' ? null : courseKey;
            const existingOrderForCourse = orderStatus.orders.find(
              (order) =>
                order.tableId &&
                order.tableId === orderInfo.tableId &&
                (order.courseId || null) === courseId &&
                order.status === ORDER_STATUS.ACCEPTED &&
                DateTime.now() < DateTime.fromISO(order.createdAt).plus({ seconds: 180 }),
            );

            const orderHasComment = orderInfo.comment && orderInfo.comment.length > 0;
            const shouldBeFired = storage.holdFirstCourse ? false : index === 0;
            let courseOrderId: string;

            if (existingOrderForCourse && !orderHasComment) {
              logger.info("[DEBUG MODE] Order existing for course, don't create new order", {
                tableId: orderInfo.tableId,
                courseId,
              });
              courseOrderId = existingOrderForCourse.id;

              if (shouldBeFired && existingOrderForCourse.fired === false) {
                orderStatusApi.fireOrder(courseOrderId);
              }
            } else {
              logger.info('[DEBUG MODE] No order existing for course, create new order', {
                tableId: orderInfo.tableId,
                courseId,
                fired: shouldBeFired,
                index,
              });
              courseOrderId = orderStatusApi.addOrder(
                orderInfo.realId || '',
                orderInfo.comment || '',
                ORDER_STATUS.ACCEPTED,
                orderInfo.serviceType,
                orderInfo.tableName || orderInfo.orderNumber,
                orderInfo.tableId,
                orderInfo.origin,
                orderInfo.desiredTime,
                courseId,
                shouldBeFired,
              );
            }

            if (!orderId) {
              orderId = courseOrderId;
            }

            logger.info('[DEBUG MODE] Add course items to KDS', {
              orderId: courseOrderId,
              courseId,
              itemCount: courseItems.length,
            });

            courseItems.forEach((item) => {
              logger.info('[DEBUG MODE] Adding item to KDS');
              orderStatusApi.addItem(
                item.itemId,
                item.categoryId,
                courseOrderId,
                item.message || '',
                item.variations.map((v) => v.id),
                item.removedVariations.map((v) => v.id),
                (item.defaultOrderStatus as ORDER_STATUS) || ORDER_STATUS.ACCEPTED,
                item.id,
                item.parentId,
                getItemCourseId(item) || undefined,
              );
            });
          });
        } else {
          const existingOrderForTable = orderStatus.orders.find(
            (order) =>
              order.tableId &&
              order.tableId === orderInfo.tableId &&
              order.status === ORDER_STATUS.ACCEPTED &&
              DateTime.now() < DateTime.fromISO(order.createdAt).plus({ seconds: 180 }),
          );

          const orderHasComment = orderInfo.comment && orderInfo.comment.length > 0;

          if (existingOrderForTable && !orderHasComment) {
            logger.info("[DEBUG MODE] Order existing for table, don't create new order");
            orderId = existingOrderForTable.id;
          } else {
            logger.info('[DEBUG MODE] No order existing for table, create new order');
            orderId = orderStatusApi.addOrder(
              orderInfo.realId || '',
              orderInfo.comment || '',
              ORDER_STATUS.ACCEPTED,
              orderInfo.serviceType,
              orderInfo.tableName || orderInfo.orderNumber,
              orderInfo.tableId,
              orderInfo.origin,
              orderInfo.desiredTime,
            );
          }

          logger.info('[DEBUG MODE] Add items to KDS', {
            orderId,
            itemsToAdd: nonServiceChargeItems,
          });

          nonServiceChargeItems.forEach((item) => {
            logger.info('[DEBUG MODE] Adding item to KDS');
            orderStatusApi.addItem(
              item.itemId,
              item.categoryId,
              orderId!,
              item.message || '',
              item.variations.map((v) => v.id),
              item.removedVariations.map((v) => v.id),
              (item.defaultOrderStatus as ORDER_STATUS) || ORDER_STATUS.ACCEPTED,
              item.id,
              item.parentId,
              getItemCourseId(item) || undefined,
            );
          });
        }
      } else {
        logger.warn(
          'Not adding items to KDS either because hasKDS is false, items length is zero or only physical bongs',
        );
      }

      // If we are letting the KDS print bongs on progress, we don't want it to print the regular first bong.
      // To distinct between the two, we use the flag onlyPhysicalBongs, which is only true on the KDS progress bongs.
      const skipRegularBongIfKDSBongOnProgress = storage.KDSBongOnProgress && !onlyPhysicalBongs;
      if (skipRegularBongIfKDSBongOnProgress) {
        logger.info('[DEBUG MODE] Not printing because we are skipping regular bongs', {
          skipRegularBongIfKDSBongOnProgress: skipRegularBongIfKDSBongOnProgress,
          kdsBongOnProgress: storage.KDSBongOnProgress,
          onlyPhysicalBongs: onlyPhysicalBongs,
        });
        onCompleted();
        return;
      }

      if (!storage.enableBongPrints) {
        logger.info('[DEBUG MODE] Not printing because bong prints are disabled', {
          enableBongPrints: storage.enableBongPrints,
        });
        onCompleted();
        return;
      }

      // Show confirmation dialog if askToPrintBong is enabled
      // Only show this once for all tickets, not per ticket
      // Skip confirmation if skipConfirmation flag is set (e.g., from Spain ParkedOrderPrintsModal)
      if (storage.askToPrintBong && !isEpos && !skipConfirmation) {
        logger.info('[BONG GROUP] Asking user to confirm bong printing');
        const shouldPrint = await confirm(i18n('ParkOrder.BongPrompt'));

        if (!shouldPrint) {
          logger.info('[BONG GROUP] User cancelled bong printing');
          onCompleted();
          return;
        }

        logger.info('[BONG GROUP] User confirmed bong printing, proceeding with print');
      }

      if (!onlyDigitalBongs) {
        logger.info('[DEBUG MODE] Printing physical bongs');
        printers.forEach((printer) => {
          printBong(printer, header, printerStationsAreUsed, orderId);
        });
      }

      onCompleted();
    };

    executeWithConfirmation();
  }, []);

  return null;
};

export default BongGroup;
